import React, { useEffect } from 'react';
import useAppStore from '../../store/useAppStore.js';
import { formatCurrencyShort } from '../../utils/amountHelpers.js';

const AdminDashboard = () => {
  const {
    admin,
    loadAdminDashboard
  } = useAppStore((state) => ({
    admin: state.admin,
    loadAdminDashboard: state.admin.loadAdminDashboard
  }));

  useEffect(() => {
    loadAdminDashboard();
  }, [loadAdminDashboard]);

  const renderStatCard = (title, value, subtitle, accent) => (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="text-sm font-semibold text-gray-500">{title}</div>
      <div className={`mt-4 text-3xl font-bold ${accent}`}>{value}</div>
      {subtitle && <div className="mt-2 text-sm text-gray-500">{subtitle}</div>}
    </div>
  );

  const renderLoadingState = () => (
    <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="h-32 rounded-2xl bg-gray-200 animate-pulse" />
      ))}
    </div>
  );

  const renderBranches = () => (
    <div className="grid gap-4 lg:grid-cols-3">
      {admin.branchPerformance.map((branch) => (
        <div key={branch.branchId} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-500">{branch.name}</div>
              <div className="mt-1 text-sm text-gray-600">{branch.location}</div>
            </div>
            <div className={`text-xs font-semibold px-2 py-1 rounded-full ${branch.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
              {branch.status}
            </div>
          </div>
          <div className="mt-4 text-2xl font-bold text-slate-900">{formatCurrencyShort(branch.revenue)}</div>
          <div className="mt-2 text-sm text-gray-500">{branch.salesCount} sales</div>
          <div className="mt-4 text-sm text-gray-500">Manager: {branch.manager}</div>
        </div>
      ))}
    </div>
  );

  const renderTopProducts = () => (
    <div className="space-y-3">
      {admin.topSellingProducts.map((product) => (
        <div key={product.id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 overflow-hidden rounded-2xl bg-slate-100">
              {product.image ? <img src={product.image} alt={product.name} className="h-full w-full object-cover" /> : null}
            </div>
            <div className="flex-1">
              <div className="text-base font-semibold text-slate-900">{product.name}</div>
              <div className="mt-1 text-sm text-gray-500">{product.quantity} sold</div>
            </div>
            <div className="text-sm font-semibold text-slate-900">{formatCurrencyShort(product.revenue)}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Overview of cross-branch performance, expenses, product demand and token activity.
          </p>
        </div>
      </div>

      {admin.isLoading ? renderLoadingState() : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {renderStatCard('Total Sales Today', formatCurrencyShort(admin.totals.today), 'All branches', 'text-blue-600')}
          {renderStatCard('Total Sales This Week', formatCurrencyShort(admin.totals.week), 'All branches', 'text-teal-600')}
          {renderStatCard('Total Sales This Month', formatCurrencyShort(admin.totals.month), 'All branches', 'text-emerald-600')}
          {renderStatCard('Active Tokens', admin.activeTokens, 'Open orders across branches', 'text-violet-600')}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Branch performance</h2>
              <p className="mt-1 text-sm text-gray-500">Compare revenue and sales by branch.</p>
            </div>
            <div className="rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">{admin.branchPerformance.length} branches</div>
          </div>
          <div className="mt-6 space-y-4">{admin.branchPerformance.length ? renderBranches() : <div className="text-gray-500">No branch performance data available.</div>}</div>
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Total expenses</h2>
              <p className="mt-1 text-sm text-gray-500">All branches combined.</p>
            </div>
            <div className="mt-6 text-4xl font-bold text-rose-600">{formatCurrencyShort(admin.totalExpenses)}</div>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Top selling products</h2>
              <p className="mt-1 text-sm text-gray-500">Most popular items across all branches.</p>
            </div>
            <div className="mt-6">{admin.topSellingProducts.length ? renderTopProducts() : <div className="text-gray-500">No sales data available.</div>}</div>
          </div>
        </div>
      </div>

      {admin.error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {admin.error}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
